import { Badge, Box, Group, Loader, Paper, Stack, Text, TextInput, Title } from "@mantine/core";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "ag-grid-enterprise";
import type { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useGetChecklistLogsQuery } from "../../../../../openapi";
import { FailedAlert } from "../../../../components";
import { useAppSelector } from "../../../../hooks";
import { selectHttpGetSingleProject } from "../../../projectSelectors";
import { IChecklistLogApi } from "@mcapps/shared/schemas/checklistLogSchema";
import { formatDateToNorwegianDate } from "../../admin/selectors";
import { selectActiveChecklist, selectChecklistLogChecklistIdForPage } from "../checklistSelectors";

const formatChecklistLogDate = (value: unknown) => {
  if (value == null) return "";
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return formatDateToNorwegianDate(date as any);
};

const CHECKLIST_LOG_COLUMN_DEFS: ColDef<IChecklistLogApi>[] = [
  {
    headerName: "Tid",
    field: "createdAt" as any,
    valueFormatter: (params) => formatChecklistLogDate(params.value),
    sort: "desc",
    width: 170,
  },
  {
    headerName: "Bruker",
    valueGetter: (params) => params.data?.user?.name ?? "",
    minWidth: 160,
    flex: 1,
  },
  { headerName: "Felt", field: "column" as any, minWidth: 150, flex: 1 },
  { headerName: "Gammel verdi", field: "oldValue" as any, minWidth: 180, flex: 1 },
  { headerName: "Ny verdi", field: "newValue" as any, minWidth: 180, flex: 1 },
  {
    headerName: "Rad",
    minWidth: 220,
    flex: 1.4,
    valueGetter: (params) => {
      const row = params.data?.rowId;
      if (row == null) return "";
      const idPart = row.itemId != null ? String(row.itemId) : "";
      const titlePart = row.title ?? "";
      if (idPart.length === 0) return titlePart;
      if (titlePart.length === 0) return idPart;
      return `${idPart} - ${titlePart}`;
    },
  },
];

const DEFAULT_CHECKLIST_LOG_COL_DEF: ColDef = {
  filter: true,
  resizable: true,
  sortable: true,
};

export const ChecklistLogsTable: React.FC = () => {
  const { checklistId: checklistObjectId } = useParams<{ checklistId: string }>();
  const [filterText, setFilterText] = useState("");
  const project = useAppSelector(selectHttpGetSingleProject);
  const projectNo = project?.maconomy?.projectNo ?? null;
  const checklist = useAppSelector((state) => selectActiveChecklist(state, checklistObjectId));
  const checklistId = useAppSelector((state) => selectChecklistLogChecklistIdForPage(state, checklistObjectId));
  const skip = !projectNo || !checklistId;

  const {
    data: logs = [],
    isFetching,
    isError,
  } = useGetChecklistLogsQuery({ projectNo: projectNo ?? 0, checklistId: checklistId ?? 0 }, { skip });

  return (
    <Stack gap="sm" h="100%" mih={0} p="sm">
      {isError === true && <FailedAlert />}
      <Paper withBorder radius={0} p="sm">
        <Group justify="space-between" align="flex-start" gap="sm">
          <div>
            <Title order={5}>Endringslogg</Title>
            <Text size="sm" c="dimmed">
              {checklist?.name ?? "Sjekkliste"}
            </Text>
          </div>
          <Group gap="xs">
            {isFetching === true && <Loader size="xs" />}
            <Badge variant="light">{logs.length} rader</Badge>
          </Group>
        </Group>
      </Paper>

      <Group justify="flex-end">
        <TextInput
          size="sm"
          placeholder="Sok i loggen"
          value={filterText}
          onChange={(event) => setFilterText(event.currentTarget.value)}
          w={220}
        />
      </Group>

      <Box className="ag-theme-quartz" style={{ flex: 1, minHeight: 360, width: "100%" }}>
        <AgGridReact<IChecklistLogApi>
          rowData={logs}
          columnDefs={CHECKLIST_LOG_COLUMN_DEFS}
          defaultColDef={DEFAULT_CHECKLIST_LOG_COL_DEF}
          quickFilterText={filterText}
          animateRows
          suppressCellFocus
          enableCellTextSelection
          rowSelection="single"
        />
      </Box>
    </Stack>
  );
};
